import { useCallback, useEffect, useState } from 'react'
import { MessageSquare } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { PublicationService } from '@/services/publicationService'
import { apiErrorMessage } from '@/services/team-service'
import type { PublicationNote } from '@/types/publication'
import { parseDbTimestamp } from '@/utils/datetime'
import * as toast from '@/lib/toast'

function authorOf(note: PublicationNote): string {
  const person = note.user ?? note.userInfo
  const name = [person?.firstName, person?.lastName].filter(Boolean).join(' ')
  return name || 'Sistema'
}

function whenOf(note: PublicationNote): string {
  const date = parseDbTimestamp(note.createdAt)
  return date
    ? date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : ''
}

/** Notas internas da publicação: lista (mais recente primeiro) e campo para nova nota. */
export function PublicationNotesPanel({ publicationId, onAdded }: { publicationId: number; onAdded?: () => void }) {
  const [notes, setNotes] = useState<PublicationNote[]>([])
  const [loading, setLoading] = useState(true)
  const [text, setText] = useState('')
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const response = await PublicationService.getNotes(publicationId)
      setNotes(response.success ? response.data : [])
    } catch {
      setNotes([])
    } finally {
      setLoading(false)
    }
  }, [publicationId])

  useEffect(() => {
    load()
  }, [load])

  const add = async () => {
    const noteText = text.trim()
    if (!noteText) return
    setSaving(true)
    try {
      await PublicationService.addNote(publicationId, noteText)
      setText('')
      await load()
      onAdded?.()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Não foi possível adicionar a nota'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <section aria-labelledby="publication-notes" className="flex flex-col gap-2">
      <h3 id="publication-notes" className="flex items-center gap-1.5 text-sm font-semibold">
        <MessageSquare className="size-4 text-muted-foreground" aria-hidden="true" /> Notas
        {notes.length > 0 && <span className="tabular-nums text-muted-foreground">({notes.length})</span>}
      </h3>
      <Textarea
        aria-label="Nova nota"
        placeholder="Adicionar nota…"
        rows={2}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <Button size="sm" variant="outline" className="self-end" disabled={saving || !text.trim()} onClick={add}>
        {saving ? 'Salvando…' : 'Adicionar nota'}
      </Button>
      {loading ? (
        <Skeleton className="h-12" />
      ) : notes.length === 0 ? (
        <p className="text-xs text-muted-foreground">Nenhuma nota.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {notes.map((note) => (
            <li key={note.id} className="rounded-md border bg-muted/40 p-2 text-sm">
              <p className="flex flex-wrap justify-between gap-x-2 text-xs text-muted-foreground">
                <span>{authorOf(note)}</span>
                <span className="tabular-nums">{whenOf(note)}</span>
              </p>
              <p className="mt-1 whitespace-pre-wrap">{note.noteText}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
